import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';
import {
  Upload,
  Layers,
  Eye,
  EyeOff,
  Plus,
  Trash2,
  ZoomIn,
  ZoomOut,
  Move,
  Circle,
  Square,
  Lightbulb,
  Droplets,
  Plug,
  Wind,
  Sofa,
  Box,
  Image,
  Wand2,
  Palette,
  ChevronLeft,
  ChevronRight,
  Settings,
  Download,
  Maximize2,
  RotateCcw,
  Loader2,
  X,
  FileText
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Layer configuration
const LAYER_CONFIG = {
  electrical: { name: 'Electrical', icon: Plug, color: '#FFA500', description: 'Outlets, switches, panels' },
  plumbing: { name: 'Plumbing', icon: Droplets, color: '#3B82F6', description: 'Pipes, drains, fixtures' },
  lighting: { name: 'Lighting', icon: Lightbulb, color: '#FBBF24', description: 'Recessed, pendants, sconces' },
  hvac: { name: 'HVAC', icon: Wind, color: '#10B981', description: 'Vents, ducts, units' },
  flooring: { name: 'Flooring', icon: Square, color: '#8B5CF6', description: 'Floor materials, patterns' },
  walls: { name: 'Walls', icon: Box, color: '#6B7280', description: 'Wall modifications' },
  furniture: { name: 'Furniture', icon: Sofa, color: '#EC4899', description: 'Furniture placement' }
};

// Element types per layer
const ELEMENT_TYPES = {
  electrical: ['outlet', 'switch', 'panel', 'junction_box'],
  plumbing: ['pipe', 'drain', 'faucet', 'toilet', 'sink', 'shower'],
  lighting: ['recessed', 'pendant', 'sconce', 'chandelier', 'track'],
  hvac: ['vent', 'return', 'thermostat', 'unit'],
  flooring: ['hardwood', 'tile', 'carpet', 'vinyl', 'stone'],
  walls: ['paint', 'wallpaper', 'accent_wall', 'molding'],
  furniture: ['sofa', 'table', 'chair', 'bed', 'cabinet', 'desk']
};

export const FloorPlans = () => {
  const { api, user } = useAuth();
  const navigate = useNavigate();
  
  // Both COO and President have full 2D/3D access
  const isCOO = user?.role === 'coo' || user?.role === 'COO';
  const isPresident = user?.role === 'president' || user?.role === 'President';
  const canEdit = isCOO || isPresident; // Both can edit floor plans
  
  // State
  const [floorPlans, setFloorPlans] = useState([]);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('2d'); // '2d' or '3d'
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showRenderModal, setShowRenderModal] = useState(false);
  const [renderLoading, setRenderLoading] = useState(false);
  
  // Layer visibility state
  const [layerVisibility, setLayerVisibility] = useState({
    electrical: true,
    plumbing: true,
    lighting: true,
    hvac: false,
    flooring: true,
    walls: true,
    furniture: true
  });
  
  // Active tool/layer for editing
  const [activeLayer, setActiveLayer] = useState('electrical');
  const [activeElementType, setActiveElementType] = useState('outlet');
  const [isPlacingElement, setIsPlacingElement] = useState(false);
  
  // Canvas state
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const canvasRef = useRef(null);
  
  // Jobs for dropdown
  const [jobs, setJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState('');
  
  // Render settings - expanded with more finishes
  const [renderSettings, setRenderSettings] = useState({
    room_type: 'living room',
    style: 'modern',
    flooring: '',
    flooring_color: '',
    wall_color: '',
    wall_finish: '',
    ceiling: '',
    lighting_type: '',
    countertops: '',
    cabinets: '',
    cabinet_hardware: '',
    trim_color: '',
    trim_style: '',
    backsplash: '',
    fixtures_finish: '',
    additional_details: ''
  });
  
  // SOW data for auto-populate
  const [sowData, setSowData] = useState(null);
  const [loadingSow, setLoadingSow] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchJobs();
  }, []);

  useEffect(() => {
    if (selectedJobId) {
      fetchFloorPlans(selectedJobId);
    }
  }, [selectedJobId]);

  const fetchJobs = async () => {
    try {
      const response = await api.get('/jobs');
      setJobs(response.data);
      if (response.data.length > 0) {
        setSelectedJobId(response.data[0].id);
      }
    } catch (error) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  // Fetch SOW data for selected job
  const fetchSowData = async (jobId) => {
    if (!jobId) return;
    setLoadingSow(true);
    try {
      const response = await api.get(`/floorplans/sow/${jobId}`);
      setSowData(response.data);
    } catch (error) {
      console.log('No SOW data found for job');
      setSowData(null);
    } finally {
      setLoadingSow(false);
    }
  };

  // Auto-populate render settings from SOW
  const populateFromSow = () => {
    if (!sowData) {
      toast.error('No SOW data available for this job');
      return;
    }
    
    setRenderSettings(prev => ({
      ...prev,
      flooring: sowData.flooring || prev.flooring,
      flooring_color: sowData.flooring_color || prev.flooring_color,
      wall_color: sowData.wall_color || prev.wall_color,
      wall_finish: sowData.wall_finish || prev.wall_finish,
      ceiling: sowData.ceiling || prev.ceiling,
      lighting_type: sowData.lighting_type || prev.lighting_type,
      countertops: sowData.countertops || prev.countertops,
      cabinets: sowData.cabinets || prev.cabinets,
      cabinet_hardware: sowData.cabinet_hardware || prev.cabinet_hardware,
      trim_color: sowData.trim_color || prev.trim_color,
      trim_style: sowData.trim_style || prev.trim_style,
      backsplash: sowData.backsplash || prev.backsplash,
      fixtures_finish: sowData.fixtures_finish || prev.fixtures_finish
    }));
    
    toast.success('Settings populated from Scope of Work');
  };

  // Fetch SOW when opening render modal
  useEffect(() => {
    if (showRenderModal && selectedJobId) {
      fetchSowData(selectedJobId);
    }
  }, [showRenderModal, selectedJobId]);

  const fetchFloorPlans = async (jobId) => {
    try {
      const response = await api.get(`/floorplans/job/${jobId}`);
      setFloorPlans(response.data);
      if (response.data.length > 0 && !selectedPlan) {
        setSelectedPlan(response.data[0]);
        initializeLayerVisibility(response.data[0]);
      }
    } catch (error) {
      console.error('Error fetching floor plans:', error);
    }
  };

  const initializeLayerVisibility = (plan) => {
    if (plan?.layers) {
      const visibility = {};
      Object.keys(LAYER_CONFIG).forEach(layer => {
        visibility[layer] = plan.layers[layer]?.visible ?? true;
      });
      setLayerVisibility(visibility);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    formData.append('job_id', selectedJobId);
    
    try {
      const response = await api.post('/floorplans/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setFloorPlans([...floorPlans, response.data]);
      setSelectedPlan(response.data);
      setShowUploadModal(false);
      toast.success('Floor plan uploaded successfully');
    } catch (error) {
      toast.error('Failed to upload floor plan');
    }
  };

  const toggleLayerVisibility = async (layerName) => {
    const newVisibility = !layerVisibility[layerName];
    setLayerVisibility({ ...layerVisibility, [layerName]: newVisibility });
    
    if (selectedPlan) {
      try {
        await api.patch(`/floorplans/${selectedPlan.id}/layers`, {
          [layerName]: { visible: newVisibility }
        });
      } catch (error) {
        console.error('Error updating layer visibility:', error);
      }
    }
  };

  const handleCanvasClick = useCallback(async (e) => {
    // Only COO can place elements
    if (!canEdit || !isPlacingElement || !selectedPlan || !canvasRef.current) return;
    
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / zoom) - pan.x;
    const y = ((e.clientY - rect.top) / zoom) - pan.y;
    
    const element = {
      type: activeElementType,
      x: Math.round(x),
      y: Math.round(y),
      rotation: 0,
      scale: 1,
      label: `${activeElementType.replace('_', ' ')} ${Date.now().toString().slice(-4)}`
    };
    
    try {
      const response = await api.post(
        `/floorplans/${selectedPlan.id}/layers/${activeLayer}/element`,
        element
      );
      
      // Update local state
      const updatedPlan = { ...selectedPlan };
      if (!updatedPlan.layers[activeLayer]) {
        updatedPlan.layers[activeLayer] = { visible: true, elements: [] };
      }
      updatedPlan.layers[activeLayer].elements.push(response.data.element);
      setSelectedPlan(updatedPlan);
      
      toast.success(`Added ${activeElementType.replace('_', ' ')}`);
    } catch (error) {
      toast.error('Failed to add element');
    }
  }, [isPlacingElement, selectedPlan, activeLayer, activeElementType, zoom, pan, api]);

  const deleteElement = async (layerName, elementId) => {
    try {
      await api.delete(`/floorplans/${selectedPlan.id}/layers/${layerName}/element/${elementId}`);
      
      // Update local state
      const updatedPlan = { ...selectedPlan };
      updatedPlan.layers[layerName].elements = updatedPlan.layers[layerName].elements.filter(
        e => e.id !== elementId
      );
      setSelectedPlan(updatedPlan);
      toast.success('Element removed');
    } catch (error) {
      toast.error('Failed to remove element');
    }
  };

  const generate3DRender = async () => {
    if (!selectedPlan) return;
    
    setRenderLoading(true);
    try {
      const response = await api.post(`/floorplans/${selectedPlan.id}/render-3d`, renderSettings);
      
      // Update local state with new render
      setSelectedPlan({ ...selectedPlan, render_3d_url: response.data.render_url });
      setViewMode('3d');
      setShowRenderModal(false);
      toast.success('3D render generated successfully!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate 3D render');
    } finally {
      setRenderLoading(false);
    }
  };

  const renderElement = (element, layerName) => {
    const config = LAYER_CONFIG[layerName];
    const size = 24;
    
    return (
      <g
        key={element.id}
        transform={`translate(${element.x}, ${element.y}) rotate(${element.rotation || 0})`}
        className={`transition-opacity ${canEdit ? 'cursor-pointer hover:opacity-75' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          // Only COO can delete elements
          if (canEdit && window.confirm(`Delete ${element.type.replace('_', ' ')}?`)) {
            deleteElement(layerName, element.id);
          }
        }}
      >
        <circle r={size/2} fill={config.color} fillOpacity={0.3} stroke={config.color} strokeWidth={2} />
        <text
          textAnchor="middle"
          dominantBaseline="middle"
          fill={config.color}
          fontSize={10}
          fontWeight="bold"
        >
          {element.type.charAt(0).toUpperCase()}
        </text>
        <title>{element.label || element.type}</title>
      </g>
    );
  };

  return (
    <div className="h-[calc(100vh-120px)] flex flex-col" data-testid="floor-plans-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        <div>
          <h1 className="font-heading text-3xl">Floor Plans</h1>
          <p className="text-gray-500 mt-1">2D/3D visualization with real-time modifications</p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Job Selector */}
          <select
            value={selectedJobId}
            onChange={(e) => {
              setSelectedJobId(e.target.value);
              setSelectedPlan(null);
            }}
            className="px-4 py-2 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
            data-testid="job-selector"
          >
            {jobs.map(job => (
              <option key={job.id} value={job.id}>{job.client_name} - {job.project_type}</option>
            ))}
          </select>
          
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-[#C6A87C] text-white font-semibold hover:bg-[#B09265] transition-colors"
            data-testid="upload-floor-plan-btn"
          >
            <Upload className="w-4 h-4" />
            Upload Plan
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Left Sidebar - Layers Panel */}
        <div className="w-64 flex-shrink-0 bg-white border border-gray-100 overflow-y-auto">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold flex items-center gap-2">
              <Layers className="w-4 h-4 text-[#C6A87C]" />
              Layers
            </h3>
          </div>
          
          <div className="p-2">
            {Object.entries(LAYER_CONFIG).map(([key, config]) => (
              <div
                key={key}
                className={`p-3 mb-1 border transition-colors cursor-pointer ${
                  activeLayer === key ? 'border-[#C6A87C] bg-[#C6A87C]/5' : 'border-transparent hover:bg-gray-50'
                }`}
                onClick={() => setActiveLayer(key)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-3 h-3 rounded-full"
                      style={{ backgroundColor: config.color }}
                    />
                    <config.icon className="w-4 h-4" style={{ color: config.color }} />
                    <span className="text-sm font-medium">{config.name}</span>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleLayerVisibility(key);
                    }}
                    className="p-1 hover:bg-gray-200 rounded"
                    data-testid={`toggle-layer-${key}`}
                  >
                    {layerVisibility[key] ? (
                      <Eye className="w-4 h-4 text-gray-600" />
                    ) : (
                      <EyeOff className="w-4 h-4 text-gray-400" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1 ml-5">{config.description}</p>
                
                {/* Element count */}
                {selectedPlan?.layers?.[key]?.elements?.length > 0 && (
                  <p className="text-xs text-[#C6A87C] mt-1 ml-5">
                    {selectedPlan.layers[key].elements.length} elements
                  </p>
                )}
              </div>
            ))}
          </div>
          
          {/* Element Type Selector - Only visible to COO */}
          {canEdit && activeLayer && (
            <div className="p-4 border-t border-gray-100">
              <h4 className="text-sm font-semibold mb-2">Add Element</h4>
              <select
                value={activeElementType}
                onChange={(e) => setActiveElementType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none mb-2"
              >
                {ELEMENT_TYPES[activeLayer]?.map(type => (
                  <option key={type} value={type}>{type.replace('_', ' ')}</option>
                ))}
              </select>
              <button
                onClick={() => setIsPlacingElement(!isPlacingElement)}
                className={`w-full py-2 text-sm font-medium transition-colors ${
                  isPlacingElement
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid="place-element-btn"
              >
                {isPlacingElement ? 'Click on Plan to Place' : 'Start Placing'}
              </button>
            </div>
          )}
        </div>

        {/* Main Canvas Area */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* View Mode Toggle */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('2d')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  viewMode === '2d' ? 'bg-[#C6A87C] text-white' : 'bg-gray-100 text-gray-700'
                }`}
                data-testid="view-mode-2d"
              >
                2D Plan
              </button>
              <button
                onClick={() => setViewMode('3d')}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  viewMode === '3d' ? 'bg-[#C6A87C] text-white' : 'bg-gray-100 text-gray-700'
                }`}
                data-testid="view-mode-3d"
              >
                3D Render
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Zoom controls */}
              <button
                onClick={() => setZoom(Math.max(0.5, zoom - 0.1))}
                className="p-2 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <ZoomOut className="w-4 h-4" />
              </button>
              <span className="text-sm font-medium w-16 text-center">{Math.round(zoom * 100)}%</span>
              <button
                onClick={() => setZoom(Math.min(3, zoom + 0.1))}
                className="p-2 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <ZoomIn className="w-4 h-4" />
              </button>
              <button
                onClick={() => { setZoom(1); setPan({ x: 0, y: 0 }); }}
                className="p-2 bg-gray-100 hover:bg-gray-200 transition-colors"
              >
                <RotateCcw className="w-4 h-4" />
              </button>
              
              {canEdit && viewMode === '2d' && selectedPlan && (
                <button
                  onClick={() => setShowRenderModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#0A0A0A] text-white text-sm font-medium hover:bg-gray-800 transition-colors"
                  data-testid="generate-3d-btn"
                >
                  <Wand2 className="w-4 h-4" />
                  Generate 3D
                </button>
              )}
            </div>
          </div>

          {/* Canvas */}
          <div
            ref={canvasRef}
            className={`flex-1 bg-gray-100 border border-gray-200 overflow-hidden relative ${
              isPlacingElement ? 'cursor-crosshair' : 'cursor-grab'
            }`}
            onClick={handleCanvasClick}
          >
            {selectedPlan ? (
              viewMode === '2d' ? (
                <div
                  style={{
                    transform: `scale(${zoom}) translate(${pan.x}px, ${pan.y}px)`,
                    transformOrigin: 'top left',
                    width: '100%',
                    height: '100%'
                  }}
                >
                  {/* Floor Plan Image */}
                  {selectedPlan.image_url && (
                    <img
                      src={selectedPlan.image_url}
                      alt={selectedPlan.name}
                      className="max-w-full max-h-full object-contain"
                      draggable={false}
                    />
                  )}
                  
                  {/* SVG Overlay for elements */}
                  <svg
                    className="absolute inset-0 w-full h-full pointer-events-none"
                    style={{ pointerEvents: isPlacingElement ? 'none' : 'auto' }}
                  >
                    {Object.entries(selectedPlan.layers || {}).map(([layerName, layerData]) => (
                      layerVisibility[layerName] && layerData.elements?.map(element => (
                        renderElement(element, layerName)
                      ))
                    ))}
                  </svg>
                </div>
              ) : (
                // 3D View
                <div className="w-full h-full flex items-center justify-center">
                  {selectedPlan.render_3d_url ? (
                    <img
                      src={selectedPlan.render_3d_url}
                      alt="3D Render"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="text-center">
                      <Image className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No 3D render generated yet</p>
                      {canEdit && (
                        <button
                          onClick={() => setShowRenderModal(true)}
                          className="px-6 py-3 bg-[#C6A87C] text-white font-medium hover:bg-[#B09265] transition-colors"
                        >
                          Generate 3D Render
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <div className="text-center">
                  <Layers className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 mb-4">No floor plan selected</p>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="px-6 py-3 bg-[#C6A87C] text-white font-medium hover:bg-[#B09265] transition-colors"
                  >
                    Upload Floor Plan
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Floor Plan Tabs */}
          {floorPlans.length > 0 && (
            <div className="flex items-center gap-2 mt-2 overflow-x-auto pb-2">
              {floorPlans.map(plan => (
                <button
                  key={plan.id}
                  onClick={() => {
                    setSelectedPlan(plan);
                    initializeLayerVisibility(plan);
                  }}
                  className={`px-4 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    selectedPlan?.id === plan.id
                      ? 'bg-[#C6A87C] text-white'
                      : 'bg-white border border-gray-200 text-gray-700 hover:border-[#C6A87C]'
                  }`}
                >
                  {plan.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Right Sidebar - Properties/Render History */}
        <div className="w-64 flex-shrink-0 bg-white border border-gray-100 overflow-y-auto">
          <div className="p-4 border-b border-gray-100">
            <h3 className="font-semibold flex items-center gap-2">
              <Settings className="w-4 h-4 text-[#C6A87C]" />
              Properties
            </h3>
          </div>
          
          {selectedPlan && (
            <div className="p-4">
              <div className="mb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Name</p>
                <p className="font-medium">{selectedPlan.name}</p>
              </div>
              
              {selectedPlan.description && (
                <div className="mb-4">
                  <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Description</p>
                  <p className="text-sm text-gray-600">{selectedPlan.description}</p>
                </div>
              )}
              
              <div className="mb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">Last Updated</p>
                <p className="text-sm text-gray-600">
                  {new Date(selectedPlan.updated_at).toLocaleDateString()}
                </p>
              </div>
              
              {/* Element Summary */}
              <div className="mb-4">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-2">Elements</p>
                <div className="space-y-1">
                  {Object.entries(selectedPlan.layers || {}).map(([layerName, layerData]) => {
                    const count = layerData.elements?.length || 0;
                    if (count === 0) return null;
                    const config = LAYER_CONFIG[layerName];
                    return (
                      <div key={layerName} className="flex items-center justify-between text-sm">
                        <span className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: config?.color }} />
                          {config?.name}
                        </span>
                        <span className="text-gray-500">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {/* Actions */}
              <div className="space-y-2 pt-4 border-t border-gray-100">
                <button className="w-full py-2 text-sm text-gray-600 hover:bg-gray-50 border border-gray-200 flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" />
                  Export Plan
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
            onClick={() => setShowUploadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-md m-4"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-heading text-xl">Upload Floor Plan</h2>
                <button onClick={() => setShowUploadModal(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleUpload} className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    placeholder="e.g., Main Floor Plan"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-200 focus:border-[#C6A87C] focus:outline-none"
                    placeholder="Optional description..."
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Floor Plan Image</label>
                  <input
                    type="file"
                    name="floor_plan_image"
                    accept="image/*"
                    required
                    className="w-full"
                  />
                  <p className="text-xs text-gray-500 mt-1">Supported: JPG, PNG, PDF</p>
                </div>
                
                <button
                  type="submit"
                  className="w-full py-3 bg-[#C6A87C] text-white font-semibold hover:bg-[#B09265] transition-colors"
                >
                  Upload Floor Plan
                </button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 3D Render Modal - Enhanced with SOW Integration */}
      <AnimatePresence>
        {showRenderModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 overflow-y-auto py-4"
            onClick={() => !renderLoading && setShowRenderModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white w-full max-w-3xl m-4 max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between sticky top-0 bg-white z-10">
                <h2 className="font-heading text-xl flex items-center gap-2">
                  <Wand2 className="w-5 h-5 text-[#C6A87C]" />
                  Generate 3D Render
                </h2>
                <button
                  onClick={() => !renderLoading && setShowRenderModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                  disabled={renderLoading}
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="p-6 space-y-6">
                {/* SOW Integration Banner */}
                <div className="bg-gradient-to-r from-[#C6A87C]/10 to-[#C6A87C]/5 border border-[#C6A87C]/30 p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold text-[#C6A87C] flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      SOW Integration
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {sowData ? 'Scope of Work data available - auto-populate finishes' : 'No SOW data found for this job'}
                    </p>
                  </div>
                  <button
                    onClick={populateFromSow}
                    disabled={!sowData || loadingSow}
                    className="px-4 py-2 bg-[#C6A87C] text-white text-sm font-medium hover:bg-[#B09265] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="populate-from-sow-btn"
                  >
                    {loadingSow ? 'Loading...' : 'Auto-Fill from SOW'}
                  </button>
                </div>

                {/* Room & Style */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">Room & Style</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Room Type</label>
                      <select
                        value={renderSettings.room_type}
                        onChange={(e) => setRenderSettings({ ...renderSettings, room_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="living room">Living Room</option>
                        <option value="bedroom">Bedroom</option>
                        <option value="master bedroom">Master Bedroom</option>
                        <option value="kitchen">Kitchen</option>
                        <option value="bathroom">Bathroom</option>
                        <option value="master bathroom">Master Bathroom</option>
                        <option value="office">Home Office</option>
                        <option value="dining room">Dining Room</option>
                        <option value="laundry room">Laundry Room</option>
                        <option value="garage">Garage</option>
                        <option value="basement">Basement</option>
                        <option value="hallway">Hallway</option>
                        <option value="foyer">Foyer/Entryway</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Design Style</label>
                      <select
                        value={renderSettings.style}
                        onChange={(e) => setRenderSettings({ ...renderSettings, style: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="modern">Modern</option>
                        <option value="contemporary">Contemporary</option>
                        <option value="traditional">Traditional</option>
                        <option value="transitional">Transitional</option>
                        <option value="minimalist">Minimalist</option>
                        <option value="industrial">Industrial</option>
                        <option value="farmhouse">Farmhouse</option>
                        <option value="coastal">Coastal/Beach</option>
                        <option value="mediterranean">Mediterranean</option>
                        <option value="mid-century modern">Mid-Century Modern</option>
                        <option value="rustic">Rustic</option>
                        <option value="scandinavian">Scandinavian</option>
                        <option value="luxury">Luxury/High-End</option>
                        <option value="bohemian">Bohemian</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Flooring */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">Flooring</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Material</label>
                      <select
                        value={renderSettings.flooring}
                        onChange={(e) => setRenderSettings({ ...renderSettings, flooring: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Auto (based on style)</option>
                        <optgroup label="Hardwood">
                          <option value="hardwood oak">Oak Hardwood</option>
                          <option value="hardwood walnut">Walnut Hardwood</option>
                          <option value="hardwood maple">Maple Hardwood</option>
                          <option value="hardwood cherry">Cherry Hardwood</option>
                          <option value="hardwood hickory">Hickory Hardwood</option>
                          <option value="engineered hardwood">Engineered Hardwood</option>
                        </optgroup>
                        <optgroup label="Tile">
                          <option value="porcelain tile">Porcelain Tile</option>
                          <option value="ceramic tile">Ceramic Tile</option>
                          <option value="marble tile">Marble Tile</option>
                          <option value="travertine tile">Travertine Tile</option>
                          <option value="slate tile">Slate Tile</option>
                          <option value="terracotta tile">Terracotta Tile</option>
                        </optgroup>
                        <optgroup label="Other">
                          <option value="luxury vinyl plank">Luxury Vinyl Plank (LVP)</option>
                          <option value="laminate">Laminate</option>
                          <option value="polished concrete">Polished Concrete</option>
                          <option value="carpet">Carpet</option>
                          <option value="bamboo">Bamboo</option>
                          <option value="cork">Cork</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Color/Finish</label>
                      <select
                        value={renderSettings.flooring_color}
                        onChange={(e) => setRenderSettings({ ...renderSettings, flooring_color: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Natural/Default</option>
                        <option value="natural light">Natural Light</option>
                        <option value="honey">Honey/Golden</option>
                        <option value="medium brown">Medium Brown</option>
                        <option value="dark espresso">Dark Espresso</option>
                        <option value="gray wash">Gray Wash</option>
                        <option value="whitewash">Whitewash</option>
                        <option value="ebony">Ebony/Black</option>
                        <option value="weathered">Weathered/Distressed</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Walls */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">Walls</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Wall Color</label>
                      <select
                        value={renderSettings.wall_color}
                        onChange={(e) => setRenderSettings({ ...renderSettings, wall_color: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Auto (based on style)</option>
                        <optgroup label="Neutrals">
                          <option value="pure white">Pure White</option>
                          <option value="swiss coffee">Swiss Coffee</option>
                          <option value="alabaster">Alabaster</option>
                          <option value="agreeable gray">Agreeable Gray</option>
                          <option value="repose gray">Repose Gray</option>
                          <option value="accessible beige">Accessible Beige</option>
                          <option value="warm beige">Warm Beige/Tan</option>
                          <option value="greige">Greige</option>
                        </optgroup>
                        <optgroup label="Colors">
                          <option value="sage green">Sage Green</option>
                          <option value="dusty blue">Dusty Blue</option>
                          <option value="navy blue">Navy Blue</option>
                          <option value="forest green">Forest Green</option>
                          <option value="terracotta">Terracotta</option>
                          <option value="blush pink">Blush Pink</option>
                          <option value="charcoal">Charcoal</option>
                          <option value="black">Black (Accent)</option>
                        </optgroup>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Wall Finish/Treatment</label>
                      <select
                        value={renderSettings.wall_finish}
                        onChange={(e) => setRenderSettings({ ...renderSettings, wall_finish: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Smooth Paint</option>
                        <option value="matte paint">Matte Paint</option>
                        <option value="eggshell paint">Eggshell Paint</option>
                        <option value="satin paint">Satin Paint</option>
                        <option value="shiplap">Shiplap</option>
                        <option value="board and batten">Board and Batten</option>
                        <option value="wainscoting">Wainscoting</option>
                        <option value="wallpaper textured">Textured Wallpaper</option>
                        <option value="accent wall wood">Wood Accent Wall</option>
                        <option value="exposed brick">Exposed Brick</option>
                        <option value="stone veneer">Stone Veneer</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Ceiling */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">Ceiling</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Ceiling Type</label>
                      <select
                        value={renderSettings.ceiling}
                        onChange={(e) => setRenderSettings({ ...renderSettings, ceiling: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Standard Flat (8ft)</option>
                        <option value="high ceiling 10ft">High Ceiling (10ft)</option>
                        <option value="high ceiling 12ft">High Ceiling (12ft)</option>
                        <option value="vaulted">Vaulted/Cathedral</option>
                        <option value="tray ceiling">Tray Ceiling</option>
                        <option value="coffered">Coffered Ceiling</option>
                        <option value="beamed wood">Wood Beamed</option>
                        <option value="exposed beams">Exposed Industrial Beams</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Lighting</label>
                      <select
                        value={renderSettings.lighting_type}
                        onChange={(e) => setRenderSettings({ ...renderSettings, lighting_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Based on floor plan elements</option>
                        <option value="recessed LED">Recessed LED</option>
                        <option value="recessed can lights">Recessed Can Lights</option>
                        <option value="pendant lights">Pendant Lights</option>
                        <option value="chandelier">Chandelier</option>
                        <option value="ceiling fan with light">Ceiling Fan with Light</option>
                        <option value="track lighting">Track Lighting</option>
                        <option value="wall sconces">Wall Sconces</option>
                        <option value="cove lighting">Cove/Indirect Lighting</option>
                        <option value="natural daylight">Natural Daylight</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Kitchen/Bathroom Specific */}
                {(renderSettings.room_type.includes('kitchen') || renderSettings.room_type.includes('bathroom')) && (
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">
                      {renderSettings.room_type.includes('kitchen') ? 'Kitchen Finishes' : 'Bathroom Finishes'}
                    </h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Countertops</label>
                        <select
                          value={renderSettings.countertops}
                          onChange={(e) => setRenderSettings({ ...renderSettings, countertops: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                        >
                          <option value="">Auto (based on style)</option>
                          <option value="white quartz">White Quartz</option>
                          <option value="calacatta quartz">Calacatta Quartz</option>
                          <option value="gray quartz">Gray Quartz</option>
                          <option value="black granite">Black Granite</option>
                          <option value="white marble">White Marble</option>
                          <option value="carrara marble">Carrara Marble</option>
                          <option value="butcher block">Butcher Block</option>
                          <option value="concrete">Concrete</option>
                          <option value="soapstone">Soapstone</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Cabinets</label>
                        <select
                          value={renderSettings.cabinets}
                          onChange={(e) => setRenderSettings({ ...renderSettings, cabinets: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                        >
                          <option value="">Auto (based on style)</option>
                          <option value="white shaker">White Shaker</option>
                          <option value="gray shaker">Gray Shaker</option>
                          <option value="navy blue">Navy Blue</option>
                          <option value="natural wood">Natural Wood</option>
                          <option value="dark espresso">Dark Espresso</option>
                          <option value="two-tone">Two-Tone (Upper/Lower)</option>
                          <option value="flat panel modern">Flat Panel Modern</option>
                          <option value="raised panel traditional">Raised Panel Traditional</option>
                          <option value="glass front">Glass Front</option>
                          <option value="open shelving">Open Shelving</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Cabinet Hardware</label>
                        <select
                          value={renderSettings.cabinet_hardware}
                          onChange={(e) => setRenderSettings({ ...renderSettings, cabinet_hardware: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                        >
                          <option value="">Auto (based on style)</option>
                          <option value="brushed nickel pulls">Brushed Nickel Pulls</option>
                          <option value="matte black pulls">Matte Black Pulls</option>
                          <option value="brass pulls">Brass/Gold Pulls</option>
                          <option value="chrome pulls">Chrome Pulls</option>
                          <option value="oil rubbed bronze">Oil Rubbed Bronze</option>
                          <option value="knobs">Round Knobs</option>
                          <option value="cup pulls">Cup Pulls</option>
                          <option value="no hardware">No Hardware (Push-to-Open)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm text-gray-600 mb-1">Backsplash</label>
                        <select
                          value={renderSettings.backsplash}
                          onChange={(e) => setRenderSettings({ ...renderSettings, backsplash: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                        >
                          <option value="">Auto (based on style)</option>
                          <option value="white subway tile">White Subway Tile</option>
                          <option value="marble slab">Marble Slab</option>
                          <option value="herringbone tile">Herringbone Tile</option>
                          <option value="zellige tile">Zellige Tile</option>
                          <option value="glass mosaic">Glass Mosaic</option>
                          <option value="penny tile">Penny Tile</option>
                          <option value="brick">Brick</option>
                          <option value="stacked stone">Stacked Stone</option>
                          <option value="full slab quartz">Full Slab Quartz</option>
                        </select>
                      </div>
                    </div>
                  </div>
                )}

                {/* Trim & Fixtures */}
                <div>
                  <h3 className="text-sm font-semibold text-gray-800 uppercase tracking-wider mb-3">Trim & Fixtures</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Trim/Molding Color</label>
                      <select
                        value={renderSettings.trim_color}
                        onChange={(e) => setRenderSettings({ ...renderSettings, trim_color: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">White (Standard)</option>
                        <option value="bright white">Bright White</option>
                        <option value="off white">Off-White/Cream</option>
                        <option value="match walls">Match Wall Color</option>
                        <option value="natural wood">Natural Wood</option>
                        <option value="dark stain">Dark Stained Wood</option>
                        <option value="black">Black</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">Trim Style</label>
                      <select
                        value={renderSettings.trim_style}
                        onChange={(e) => setRenderSettings({ ...renderSettings, trim_style: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Standard Baseboards</option>
                        <option value="tall baseboards">Tall Baseboards (6"+)</option>
                        <option value="crown molding">Crown Molding</option>
                        <option value="picture rail">Picture Rail Molding</option>
                        <option value="chair rail">Chair Rail</option>
                        <option value="modern minimal">Modern Minimal</option>
                      </select>
                    </div>
                    <div className="col-span-2">
                      <label className="block text-sm text-gray-600 mb-1">Fixture Finish (Faucets, Handles, etc.)</label>
                      <select
                        value={renderSettings.fixtures_finish}
                        onChange={(e) => setRenderSettings({ ...renderSettings, fixtures_finish: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                      >
                        <option value="">Auto (based on style)</option>
                        <option value="brushed nickel">Brushed Nickel</option>
                        <option value="matte black">Matte Black</option>
                        <option value="polished chrome">Polished Chrome</option>
                        <option value="brushed gold">Brushed Gold/Champagne Bronze</option>
                        <option value="polished brass">Polished Brass</option>
                        <option value="oil rubbed bronze">Oil Rubbed Bronze</option>
                        <option value="stainless steel">Stainless Steel</option>
                        <option value="unlacquered brass">Unlacquered Brass</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Additional Details */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Additional Details</label>
                  <textarea
                    value={renderSettings.additional_details}
                    onChange={(e) => setRenderSettings({ ...renderSettings, additional_details: e.target.value })}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 text-sm focus:border-[#C6A87C] focus:outline-none"
                    placeholder="e.g., large windows with natural light, built-in bookshelves, fireplace with marble surround, specific furniture placement..."
                  />
                </div>
                
                <div className="bg-[#C6A87C]/10 border border-[#C6A87C]/20 p-4 text-sm text-gray-600">
                  <p className="font-medium text-[#C6A87C] mb-1">AI-Powered Rendering</p>
                  <p>The 3D render will incorporate your selected finishes, elements from floor plan layers (outlets, lighting fixtures, etc.), and SOW specifications into a photorealistic visualization.</p>
                </div>
                
                <button
                  onClick={generate3DRender}
                  disabled={renderLoading}
                  className="w-full py-4 bg-[#0A0A0A] text-white font-semibold hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  data-testid="generate-render-btn"
                >
                  {renderLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Generating... (may take up to 60 seconds)
                    </>
                  ) : (
                    <>
                      <Wand2 className="w-5 h-5" />
                      Generate 3D Render
                    </>
                  )}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
